import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, MessageCircleQuestion, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { api, formatApiError } from '../lib/api';
import { mockData } from '../mock';
import { fieldCls, labelCls } from '../components/member/ProfileForm';
import { Seo, breadcrumb } from '../components/Seo';
import { useT } from '../context/LanguageContext';

const Contact = () => {
  const { t } = useT();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) return toast.error(t('Skriv litt meir i meldinga.'));
    setSending(true);
    try {
      await api.post('/contact', form);
      setSent(true);
    } catch (err) {
      toast.error(t(formatApiError(err, 'Kunne ikkje sende. Prøv igjen.')));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-black overflow-hidden" data-testid="contact-page">
      <Seo title={t('Spørsmål og kontakt | Refuel Weekend')} description={t('Har du spørsmål om Refuel Weekend 2027? Send oss ei melding om program, påmelding, overnatting eller aktivitetar, så svarar vi deg.')} path="/sporsmal" jsonLd={breadcrumb([{ name: 'Refuel Weekend', path: '/' }, { name: t('Spørsmål'), path: '/sporsmal' }])} />
      <div className="absolute inset-0" style={{ backgroundImage: `url(${mockData.heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/75 to-black/95" />
      <Link to="/" data-testid="contact-back-link" className="absolute top-6 left-6 z-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 border border-white/15 text-white hover:bg-black/70 transition-colors text-sm">
        <ArrowLeft size={16} /> {t('Tilbake')}
      </Link>

      <div className="relative min-h-screen flex items-center justify-center p-4 py-20">
        <div className="w-full max-w-xl">
          <div className="flex justify-center mb-8"><Logo size="md" /></div>
          <div className="rounded-3xl bg-[#0f0a17]/95 backdrop-blur-xl border border-white/10 p-8 md:p-10 shadow-2xl shadow-black/50">
            {sent ? (
              <div className="text-center py-6" data-testid="contact-success">
                <CheckCircle2 size={56} className="text-[#ff2e2e] mx-auto mb-4" />
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Takk for spørsmålet!')}</h1>
                <p className="text-white/60">{t('Vi har fått meldinga di og svarar deg på')} <span className="text-white">{form.email}</span> {t('så snart vi kan.')}</p>
                <Link to="/" className="inline-block mt-8 px-8 py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors">{t('Til framsida')}</Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-full bg-[#ff2e2e]/15 text-[#ff2e2e] flex items-center justify-center mx-auto mb-4"><MessageCircleQuestion size={28} /></div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-2" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Spørsmål?')}</h1>
                  <p className="text-white/60">{t('Lurer du på noko om Refuel Weekend? Send oss ei melding, så svarar vi deg.')}</p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>{t('Namn')}</label>
                      <input data-testid="contact-name-input" value={form.name} onChange={set('name')} className={fieldCls} placeholder="Ola Nordmann" required />
                    </div>
                    <div>
                      <label className={labelCls}>{t('Telefon (valfritt)')}</label>
                      <input data-testid="contact-phone-input" value={form.phone} onChange={set('phone')} className={fieldCls} placeholder="+47 000 00 000" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t('E-post')}</label>
                    <input data-testid="contact-email-input" type="email" value={form.email} onChange={set('email')} className={fieldCls} placeholder="deg@epost.no" required />
                  </div>
                  <div>
                    <label className={labelCls}>{t('Spørsmålet ditt')}</label>
                    <textarea data-testid="contact-message-input" value={form.message} onChange={set('message')} rows={5} className={`${fieldCls} resize-none`} placeholder={t('Skriv spørsmålet ditt her…')} required minLength={5} />
                  </div>
                  <button type="submit" data-testid="contact-submit-button" disabled={sending} className="w-full py-4 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] transition-colors shadow-lg shadow-[#ff2e2e]/30 disabled:opacity-60 flex items-center justify-center gap-2">
                    {sending ? t('Sender…') : t('Send spørsmål')} {!sending && <Send size={18} />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
